import { createSelector } from 'reselect';

import { DEFAULT_CREATE_SEND_CRM_STATE } from '../../../constants/createSendCrm';
import { NONE } from '../../../constants/integrations';
import { ICreateSendCrmLink } from '../../../types/createSendCrm';
import { CreateSendMethodEnum } from '../../../types/createSendFlow';
import { IAppState } from '../../reducers';
import { selectIsCrmSkipAllowedToUser } from '../../selectors/auth';
import { selectCurrentOrganizationCRMType, selectIsSkipCrmAllowedInOrg } from '../../selectors/organizations';
import { selectCreateSendMethod } from './method';
import { selectIsCsOrgSettingsResolved } from './orgSettings';

export const selectCsCrmState = (state: IAppState) => state.createSend.crm ?? DEFAULT_CREATE_SEND_CRM_STATE;

export const selectCsCrmByRecipientUid = createSelector(selectCsCrmState, (crm) => crm.byRecipientUid);

export const selectCsPendingCrmLink = createSelector(selectCsCrmState, (crm) => crm.pending);

export const selectCsShipmentCrmLink = createSelector(selectCsCrmState, (crm) => crm.shipment);

export const selectIsCsCrmOrg = createSelector(
  selectCurrentOrganizationCRMType,
  (crmType): boolean => typeof crmType !== 'undefined' && crmType !== NONE,
);

export const selectIsCsCrmRequirementUnresolved = createSelector(
  selectIsCsOrgSettingsResolved,
  selectCreateSendMethod,
  (isResolved, method): boolean => !isResolved && method === CreateSendMethodEnum.Recipients,
);

export const selectIsCsCrmSkipEnabled = createSelector(
  selectIsCrmSkipAllowedToUser,
  selectIsSkipCrmAllowedInOrg,
  (allowedToUser, allowedInOrg): boolean => Boolean(allowedToUser) && Boolean(allowedInOrg),
);

export const selectIsCsCrmSkippable = createSelector(
  selectCreateSendMethod,
  selectIsCsCrmSkipEnabled,
  (method, skipEnabled): boolean => method === CreateSendMethodEnum.Location || skipEnabled,
);

export const selectIsCsCrmRequiredForMethod = createSelector(
  selectIsCsCrmOrg,
  selectCreateSendMethod,
  selectIsCsCrmSkippable,
  (isCrmOrg, method, isSkippable): boolean => isCrmOrg && method === CreateSendMethodEnum.Recipients && !isSkippable,
);

export const selectCsCrmOpportunityTarget = createSelector(
  selectCreateSendMethod,
  selectCsShipmentCrmLink,
  (method, shipment): ICreateSendCrmLink | null => (method === CreateSendMethodEnum.Location ? shipment : null),
);
